import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { useAtom } from 'jotai';
import { Box, useMediaQuery } from '@mui/material';
import FileViewerModal, {
  FileViewerModalRef,
} from '@/components/FileViewer/FileViewerModal';
import MobileBottomSheet from '@/components/@IntranetLibrary/MobileBottomSheet';
import {
  FileViewerStateAtom,
  INIT_FILE_VIEWER_STATE,
} from '@/recoil/atom/fileViewer';
import { detectFileType } from '@/components/FileViewer/utils/fileTypeDetector';
import UnsupportedFileView from '@/components/FileViewer/Viewer/UnsupportedFileView';
import FileViewerHeader from '@/components/FileViewer/Header/FileViewerHeader';
import CollapsibleLayout from '@/components/Document/CollapsibleLayout';
import { FileViewerAIPanel } from '@/components/FileViewer/AIPanel';
import ImageViewer from '@/components/FileViewer/Viewer/renderers/ImageViewer';
import SpreadsheetViewer from '@/components/FileViewer/Viewer/renderers/SpreadsheetViewer';
import TextFileViewer from '@/components/FileViewer/Viewer/renderers/TextFileViewer';
import HtmlViewer from '@/components/FileViewer/Viewer/renderers/HtmlViewer';
import FloatingToolbar from '@/components/FileViewer/Toolbar/FloatingToolbar';
import { PAGE_URL } from '@/constant/url';

const FileViewerContainer: React.FC = () => {
  const router = useRouter();
  const [viewerState, setViewerState] = useAtom(FileViewerStateAtom);
  const [hasFileError, setHasFileError] = useState(false);

  // /file-viewer 페이지는 같은 atom(FileViewerStateAtom)으로 뷰어(헤더/본문/AI 바텀시트)를
  // 직접 렌더한다. _app 전역에 있는 이 컨테이너까지 함께 렌더되면 모달 뷰어와 바텀시트가
  // 2중으로 뜬다(PC 에서 첨부파일 열기 → 새 탭 /file-viewer → 창을 줄여 MO 전환 시나리오).
  // 두 시트가 겹쳐 있어 위쪽 시트를 드래그로 내려도 뒤쪽 인스턴스의 시트가 그대로 남는다.
  const isFileViewerPage = router.pathname === PAGE_URL.FILE_VIEWER;

  const fileUuid = viewerState.file?.uuid ?? '';
  const modalRef = useRef<FileViewerModalRef>(null);
  const imageContainerRef = useRef<HTMLDivElement>(null);

  // PC/Mobile 분기
  const isMobile = useMediaQuery('(max-width:768px)');

  const handleFileError = useCallback(() => {
    setHasFileError(true);
  }, []);

  // 모달 열기/닫기 동기화
  useEffect(() => {
    if (viewerState.isOpen) {
      modalRef.current?.open();
    } else {
      modalRef.current?.close();
    }
  }, [viewerState.isOpen, isMobile]);

  const handleClose = () => {
    setViewerState(INIT_FILE_VIEWER_STATE);
    setHasFileError(false);
  };

  const handleToggleAiPanel = () => {
    setViewerState((prev) => ({
      ...prev,
      isAiPanelOpen: !prev.isAiPanelOpen,
    }));
  };

  // AI 패널 '닫기' 전용 핸들러(멱등).
  // 바텀시트를 드래그로 닫으면 SwipeableDrawer 의 onClose 가 swipe 종료 +
  // 백드롭 클릭으로 연속 호출될 수 있다. 닫기 콜백에 토글(handleToggleAiPanel)을
  // 쓰면 false→true 로 되돌아가 시트가 다시 열리고, keepMounted 로 DOM 에 남아
  // 있던 시트와 겹쳐 '바텀시트 뒤에 바텀시트'로 중복 노출된다. 항상 닫힘으로 고정한다.
  const handleCloseAiPanel = () => {
    setViewerState((prev) => {
      if (!prev.isAiPanelOpen) return prev;
      return { ...prev, isAiPanelOpen: false };
    });
  };

  // 뷰어 설정 핸들러
  const handleZoomIn = () => {
    setViewerState((prev) => ({
      ...prev,
      settings: {
        ...prev.settings,
        zoom: Math.min(prev.settings.zoom + 25, 200),
      },
    }));
  };

  const handleZoomOut = () => {
    setViewerState((prev) => ({
      ...prev,
      settings: {
        ...prev.settings,
        zoom: Math.max(prev.settings.zoom - 25, 25),
      },
    }));
  };

  const handleRotate = () => {
    setViewerState((prev) => ({
      ...prev,
      settings: {
        ...prev.settings,
        rotation: prev.settings.rotation + 90,
      },
    }));
  };

  const handleToggleViewMode = () => {
    setViewerState((prev) => {
      const newViewMode =
        prev.settings.viewMode === 'fit' ? 'fullscreen' : 'fit';
      return {
        ...prev,
        settings: {
          ...prev.settings,
          viewMode: newViewMode,
        },
        // 전체화면 모드일 때는 AI 패널 닫기
        isAiPanelOpen:
          newViewMode === 'fullscreen' ? false : prev.isAiPanelOpen,
      };
    });
  };

  const fileType = viewerState.file
    ? detectFileType(viewerState.file.name)
    : 'unsupported';
  const fileName = viewerState.file?.name ?? '';
  const fileUrl = viewerState.file?.link ?? '';
  const isAiAvailable = fileType !== 'unsupported' && !hasFileError;

  const renderContent = () => {
    // 미지원 파일
    if (fileType === 'unsupported') {
      return <UnsupportedFileView fileName={fileName} />;
    }

    // 이미지 파일은 ImageViewer와 FloatingToolbar 사용
    if (fileType === 'image') {
      return (
        <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
          <ImageViewer
            fileUrl={fileUrl}
            fileName={fileName}
            settings={viewerState.settings}
            containerRef={imageContainerRef}
            onError={handleFileError}
          />
          <Box className="file-viewer-print-hide">
            <FloatingToolbar
              settings={viewerState.settings}
              onZoomIn={handleZoomIn}
              onZoomOut={handleZoomOut}
              onRotate={handleRotate}
              onToggleViewMode={handleToggleViewMode}
              isAiPanelOpen={viewerState.isAiPanelOpen}
              isMobile={isMobile}
            />
          </Box>
        </Box>
      );
    }

    // 스프레드시트 파일 (csv, xls, xlsx)은 SpreadsheetViewer와 FloatingToolbar 사용
    if (fileType === 'csv' || fileType === 'xls') {
      return (
        <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
          <SpreadsheetViewer
            fileUrl={fileUrl}
            fileName={fileName}
            settings={viewerState.settings}
            containerRef={imageContainerRef}
            onError={handleFileError}
          />
          <Box className="file-viewer-print-hide">
            <FloatingToolbar
              settings={viewerState.settings}
              onZoomIn={handleZoomIn}
              onZoomOut={handleZoomOut}
              onRotate={handleRotate}
              onToggleViewMode={handleToggleViewMode}
              isAiPanelOpen={viewerState.isAiPanelOpen}
              isMobile={isMobile}
              disableRotate
            />
          </Box>
        </Box>
      );
    }

    // 마크다운/텍스트 파일은 TextFileViewer로 렌더링
    if (fileType === 'markdown' || fileType === 'text') {
      return (
        <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
          <TextFileViewer
            fileUrl={fileUrl}
            fileName={fileName}
            fileType={fileType}
            settings={viewerState.settings}
            containerRef={imageContainerRef}
            onError={handleFileError}
          />
          <Box className="file-viewer-print-hide">
            <FloatingToolbar
              settings={viewerState.settings}
              onZoomIn={handleZoomIn}
              onZoomOut={handleZoomOut}
              onRotate={handleRotate}
              onToggleViewMode={handleToggleViewMode}
              isAiPanelOpen={viewerState.isAiPanelOpen}
              isMobile={isMobile}
              disableRotate
            />
          </Box>
        </Box>
      );
    }

    // HTML 파일은 iframe으로 렌더링
    if (fileType === 'html') {
      return (
        <HtmlViewer
          fileUrl={fileUrl}
          fileName={fileName}
          onError={handleFileError}
        />
      );
    }

    // 나머지 파일은 Google Docs Viewer로 렌더링
    const encodedUrl = encodeURIComponent(fileUrl);
    const viewerUrl = `https://docs.google.com/gview?url=${encodedUrl}&embedded=true`;

    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          // 모바일: 여백 없이 꽉 차게 + iframe 내부 터치 스크롤 허용
          padding: isMobile ? 0 : '32px 48px',
          boxSizing: 'border-box',
          overflow: isMobile ? 'auto' : 'visible',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        <iframe
          src={viewerUrl}
          style={{
            borderRadius: isMobile ? 0 : '8px',
            border: isMobile
              ? 'none'
              : '1px solid var(--GrayScale-gray_3, #393D41)',
            background: 'var(--GrayScale-gray_White, #2B2D2F)',
            boxShadow: isMobile
              ? 'none'
              : '0 0 16px 0 var(--effect-black-16, rgba(255, 255, 255, 0.16))',

            width: '100%',
            height: '100%',
          }}
          title={fileName}
        />
      </Box>
    );
  };

  if (isFileViewerPage || !viewerState.file) return null;

  return (
    <>
      <FileViewerModal ref={modalRef} onClose={handleClose}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            width: '100%',
          }}
        >
          {/* 헤더 */}
          <Box className="file-viewer-print-hide">
            <FileViewerHeader
              fileName={viewerState.file.name}
              fileUrl={viewerState.file.link}
              fileUuid={fileUuid}
              isAiPanelOpen={viewerState.isAiPanelOpen}
              onToggleAiPanel={handleToggleAiPanel}
              isMobile={isMobile}
              onClose={handleClose}
              disableAiPanel={!isAiAvailable}
            />
          </Box>

          {/* 본문 영역 — 헤더(PC 65px) 아래 남는 높이만 차지한다.
              CollapsibleLayout 내부 root 가 height:100% 라, 여기서 전체 높이를
              물려주면 헤더 높이만큼 넘쳐 본문에 스크롤이 생긴다. */}
          <Box sx={{ flex: 1, minHeight: 0 }}>
            <CollapsibleLayout
              isDrawerOpen={
                !isMobile && isAiAvailable && viewerState.isAiPanelOpen
              }
              layoutType="modal"
              menuTopOffset={isMobile ? 0 : 65}
              menuWidth="400px"
              keepMounted
              drawerStyle={{
                borderLeft: 'none',
                backgroundColor: 'transparent',
              }}
              menu={
                isAiAvailable ? (
                  <Box
                    className="file-viewer-print-hide"
                    sx={{ height: '100%' }}
                  >
                    <FileViewerAIPanel
                      onCloseList={handleCloseAiPanel}
                      isMobile={false}
                      fileUuid={fileUuid}
                      fileName={fileName}
                    />
                  </Box>
                ) : null
              }
            >
              <Box
                className="file-viewer-print-content"
                sx={{
                  height: '100%',
                  overflow: 'hidden',
                }}
              >
                {renderContent()}
              </Box>
            </CollapsibleLayout>
          </Box>
        </Box>
      </FileViewerModal>

      {/* 모바일: AI Bottom Sheet - Modal 밖에서 렌더링.
          드로어는 body로 portal 되므로 부모 Box의 CSS 셀렉터로는 z-index를 줄 수 없어
          drawer 자체(zIndex prop)에 지정한다. 값 근거는 아래 zIndex prop 주석 참고. */}
      {isMobile && isAiAvailable && (
        <MobileBottomSheet
          open={viewerState.isAiPanelOpen}
          onClose={handleCloseAiPanel}
          variant="dark"
          height="calc(100dvh - 84px)"
          heightMode="sheet"
          showHandle={false}
          notifyAppBar
          // FileViewerModal(1300) 위, MUI Snackbar(1400) 아래에 위치시켜
          // 시트 안에서 뜨는 토스트(복사/내보내기)가 가려지지 않게 한다.
          zIndex={1350}
        >
          <FileViewerAIPanel
            isMobile={true}
            fileUuid={fileUuid}
            fileName={fileName}
            onCloseList={handleCloseAiPanel}
          />
        </MobileBottomSheet>
      )}
    </>
  );
};

export default FileViewerContainer;
